import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import DisplayOnViewportIntersection from "@/bit/components/next13.components.display-on-viewport-intersection";
import CustomizerComponent from "@/bit/components/next13.components.customizer-component";
import MetaData from "@/bit/core/next13.core.meta-data";
import Header from "@/bit/components/next13.components.header";
import Footer from "@/bit/components/next13.components.footer";
import { wrapper } from "store";
import { END } from "redux-saga";
import Banner from "@/component/banner";
import FeaturedOffer from "@/component/featured-offer";
import DeviceSection from "@/component/devices-section";
import Teletipo from "@/component/teletipo";
import { useSelector } from "react-redux";

const AdvantagesSection = dynamic(() =>
  import("@/component/advantages-section")
);
const StreamingPlatformsSection = dynamic(() =>
  import("@/component/streaming-platforms-section")
);
const Ecosystem = dynamic(() => import("@/component/ecosystem"));

const Home = (props) => {
  const {
    breadcrumbsProps,
    footerProps,
    headerProps,
    seoInfo,
    // creatividades
    teletipo,
    banner1,
    banner2,
    banner3,
    offerTitle,
    offer1,
    offer2,
    offer3,
    offer4,
    sliderContenidos,
    rendToRendDevices,
    freeDevices,
    advantages,
    ecosystem,
    loggedActivityNames,
    noLoggedActivityNames,
  } = props;

  const {
    userIdPartyHash,
    userCUCFijoHash,
    userCUCMovilHash,
    userCUCPrepagoHash,
  } = useSelector((state) => state.main.context);

  const activities = useMemo(() => {
    const isLogged =
      userIdPartyHash ||
      userCUCFijoHash ||
      userCUCMovilHash ||
      userCUCPrepagoHash ||
      "";

    return isLogged ? loggedActivityNames : noLoggedActivityNames;
  }, [userIdPartyHash, userCUCFijoHash, userCUCMovilHash, userCUCPrepagoHash]);

  return (
    <>
      <MetaData seoInfo={seoInfo} />
      <Header {...breadcrumbsProps} {...headerProps} />

      <CustomizerComponent
        name={activities.teletipo}
        Component={Teletipo}
        componentName="Teletipo"
        componentProps={teletipo}
        skeletonClassName="teletipo-skeleton"
      />
      {banner1 && banner2 && banner3 && (
        <Banner
          bannerProps={[banner1, banner2, banner3]}
          activityNames={[
            activities.banner1,
            activities.banner2,
            activities.banner3,
          ]}
        />
      )}
      <FeaturedOffer
        offerProps={[offer1, offer2, offer3, offer4]}
        offerActivities={[
          activities.offer1,
          activities.offer2,
          activities.offer3,
          activities.offer4,
        ]}
        title={offerTitle}
        titleActivity={activities.offerTitle}
      />

      <DisplayOnViewportIntersection options={{ rootMargin: "500px" }}>
        <CustomizerComponent
          name={activities.ecosystem}
          Component={Ecosystem}
          componentProps={ecosystem}
          componentName="Ecosystem"
          skeletonClassName="skeleton-ecosystem"
        />
      </DisplayOnViewportIntersection>
      <DisplayOnViewportIntersection options={{ rootMargin: "500px" }}>
        <DeviceSection
          rendToRendDevices={rendToRendDevices}
          rendToRendActivityName={activities.rendToRendDevices}
          freeDevices={freeDevices}
          freeDevicesActivityName={activities.freeDevices}
        />
      </DisplayOnViewportIntersection>

      <DisplayOnViewportIntersection options={{ rootMargin: "500px" }}>
        <CustomizerComponent
          name={activities.sliderContenidos}
          Component={StreamingPlatformsSection}
          componentName="StreamingPlatformSection"
          componentProps={sliderContenidos}
        />
      </DisplayOnViewportIntersection>
      <CustomizerComponent
        name={activities.advantages}
        Component={AdvantagesSection}
        componentName="StreamingPlatformSection"
        componentProps={advantages}
      />
      <Footer footerProps={footerProps} />
    </>
  );
};

export default Home;

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const modules = await Promise.all([
    import("@gerenciatd/next13.adabyron.misc.get-static-content"),
    import("@gerenciatd/next13.normalizers.components-header.sections"),
    import("@gerenciatd/next13.normalizers.components-header.breadcrumbs"),
    import("@/bit/reducers/next13.reducers.header-reducer"),
    import("@gerenciatd/next13.adabyron.custo.get-seo"),
    import("@gerenciatd/next13.adabyron.custo.get-by-name"),
    import("@gerenciatd/next13.adabyron.custo.get-page"),
  ]);
  const getStaticContent = modules[0].default;
  const getHeaderSections = modules[1].default;
  const getBreadcrumbs = modules[2].default;
  const headerActions = modules[3].actions;
  const getSeo = modules[4].default;
  const getPage = modules[6].getPage;
  const context = store.getState().main.context;

  const [noLoggedPage, loggedPage] = await Promise.all([
    getPage({
      name: "CHAMELEON_HOME_NOLOGADO",
      build: true,
      context,
    }),
    getPage({
      name: "CHAMELEON_HOME_LOGADO",
      build: true,
      context,
    }),
  ]);

  const creaIndex = {
    teletipo: 0,
    banner1: 1,
    banner2: 2,
    banner3: 3,
    offerTitle: 4,
    offer1: 5,
    offer2: 6,
    offer3: 7,
    offer4: 8,
    ecosystem: 9,
    rendToRendDevices: 10,
    freeDevices: 11,
    sliderContenidos: 12,
    advantages: 13,
  };

  // funciona como un map, pero para objetos
  const objectMap = (obj, fn) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)])
    );

  const noLoggedKeys = Object.keys(noLoggedPage);
  const loggedKeys = Object.keys(loggedPage);

  // actividades de la página no logado
  const pageActivities = objectMap(
    creaIndex,
    (value) => noLoggedPage[noLoggedKeys[value]]
  );

  // nombre de las actividades logado
  const loggedActivityNames = objectMap(
    creaIndex,
    (value) => loggedKeys[value]
  );

  // nombre de las actividades no logado
  const noLoggedActivityNames = objectMap(
    creaIndex,
    (value) => noLoggedKeys[value]
  );

  const seoInfo = await getSeo({
    context,
    location: "www.movistar.es/particulares",
  });

  // HEADER USUAL PROPS
  const urlBreadcrumbs = "/static/json/header/breadcrumbs.json";
  const breadcrumbsInfoJson = await getStaticContent({
    resourcePath: urlBreadcrumbs,
    context,
  });
  const url = "";
  const breadcrumbsProps = getBreadcrumbs(breadcrumbsInfoJson, url);
  const headerInfoJson = await getStaticContent({
    resourcePath: "/static/json/header/header_plan_r.json",
    context,
  });

  // HEADER REDUX PROPS
  const headerProps = getHeaderSections(headerInfoJson, breadcrumbsProps);
  store.dispatch(headerActions.setupHeaderProps(headerProps));

  // FOOTER USUAL PROPS
  const footerProps = await getStaticContent({
    resourcePath: "/static/json/footer/footer_Plan_R.json",
    context,
  });
  store.dispatch(END);
  await store.sagaTask.toPromise();

  return {
    props: {
      seoInfo,
      headerProps,
      breadcrumbsProps,
      footerProps,
      loggedActivityNames,
      noLoggedActivityNames,
      ...pageActivities,
    },
  };
});
